import React from "react";
import { Link } from "react-router-dom";
import {
  Award,
  BookOpenCheck,
  CreditCard,
  FileText,
  Gavel,
  Mail,
  Phone,
  ShieldAlert,
  UserCheck,
} from "lucide-react";
import MarketingHeader from "../components/marketing/MarketingHeader";
import Footer from "../components/marketing/Footer";

const eligibility = [
  "You must be at least 16 years old to use the platform.",
  "You must be currently enrolled in, or associated with, a recognized educational institution where the selected program requires student eligibility.",
  "You must provide accurate personal, academic, guardian, and contact information during registration.",
  "If you are under 18 years old, you must use the platform with parent or guardian consent.",
  "You must follow applicable academic, institutional, UGC, NEP-2020, and program-specific internship guidelines.",
];

const accountResponsibilities = [
  "You must provide complete and accurate registration information.",
  "You are responsible for keeping your login details confidential.",
  "You must notify Kampus Campanion immediately if you suspect unauthorized account access.",
  "Only one student account is allowed per learner unless Kampus Campanion gives written approval.",
  "You are responsible for all activity carried out through your account.",
  "You must keep your profile, academic, and contact information updated.",
];

const completionRequirements = [
  "Complete the assigned course modules, attendance requirements, tasks, and learning activities.",
  "Submit required assignments, projects, reports, or assessments within the stated timeframe.",
  "Maintain the minimum assessment or completion standards communicated for the program.",
  "Follow academic integrity rules and avoid plagiarism, impersonation, or dishonest submissions.",
  "Complete the program within the stipulated duration unless an extension is approved.",
];

const prohibitedConduct = [
  "Submitting plagiarized, copied, or AI-generated work without permitted disclosure or attribution.",
  "Sharing assessment answers, login credentials, course materials, certificates, or restricted platform access.",
  "Impersonating another person or providing false identity, academic, payment, or certificate information.",
  "Attempting to hack, reverse engineer, overload, scrape, spam, or misuse the platform.",
  "Uploading malicious content, abusive messages, illegal material, or content that violates another person's rights.",
  "Using Kampus Campanion content or services for unauthorized commercial purposes.",
];

const terminationReasons = [
  "Violation of these Terms of Service.",
  "Academic dishonesty, plagiarism, impersonation, or certificate fraud.",
  "Non-payment, failed payment verification, chargebacks, or payment disputes.",
  "Illegal, fraudulent, abusive, or harmful activity.",
  "Misuse of platform systems, course content, admin workflows, or certificate verification features.",
];

const TermsOfServicePage = () => (
  <div className="min-h-screen bg-slate-50">
    <MarketingHeader />

    <section className="bg-slate-900 text-white py-20">
      <div className="marketing-container max-w-4xl">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-3">Terms of Service</div>
        <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight">
          Terms for using Kampus Campanion.
        </h1>
        <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-3xl">
          These terms explain the rules for registration, payment, internships, certificates, platform use, and account responsibilities on Kampus Campanion.
        </p>
        <p className="mt-4 text-sm text-slate-400">Last updated: May 29, 2026</p>
      </div>
    </section>

    <section className="section-padding">
      <div className="marketing-container max-w-5xl space-y-10">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: UserCheck, title: "Student Eligibility", text: "Learners must provide truthful academic information and meet age, consent, and institution requirements." },
            { icon: CreditCard, title: "No Refund Policy", text: "Program fees are non-refundable once payment is processed and course access is provided." },
            { icon: Award, title: "Verified Certificates", text: "Certificates are issued after successful completion and can be verified through the platform." },
          ].map((item) => (
            <div key={item.title} className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center mb-4">
                <item.icon className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="font-heading font-semibold text-lg text-slate-900">{item.title}</h2>
              <p className="text-sm text-slate-600 leading-relaxed mt-2">{item.text}</p>
            </div>
          ))}
        </div>

        <TermsBlock icon={FileText} title="1. Acceptance of Terms">
          <p>
            By accessing, registering for, or using Kampus Campanion, you agree to be bound by these Terms of Service. If you do not agree with these terms, you should not access or use the platform.
          </p>
          <p>
            Kampus Campanion is a unit of Dmaskut Private Limited and provides structured internship programs, student dashboards, payment workflows, attendance and assignment tracking, and verifiable digital certificates.
          </p>
        </TermsBlock>

        <TermsBlock icon={UserCheck} title="2. Eligibility">
          <TermsList items={eligibility} />
        </TermsBlock>

        <TermsBlock icon={ShieldAlert} title="3. Registration and Account Responsibilities">
          <TermsList items={accountResponsibilities} />
          <p>
            Kampus Campanion may disable, suspend, or restrict accounts that appear inaccurate, duplicate, unsafe, fraudulent, or in violation of these terms.
          </p>
        </TermsBlock>

        <TermsBlock icon={CreditCard} title="4. Payments, Fees, and Refunds">
          <p>
            Paid internship programs currently carry a registration fee of INR 315 for most colleges. Some listed exception colleges carry a fee of INR 515, and the final payable amount is shown during checkout. The fee covers course access, learning workflows, mentorship support where applicable, attendance and assignment tracking, and certificate processing after eligible completion.
          </p>
          <p>
            Payment is required before course access is granted, except where Kampus Campanion approves a valid free coupon or sponsored access. Payments are processed through secure third-party payment gateways such as Razorpay.
          </p>
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-5">
            <h3 className="font-heading text-lg font-semibold text-rose-900">No Refund Policy</h3>
            <p className="text-rose-800 mt-2">
              Once payment is processed and course access is provided, fees are non-refundable because educational resources, platform access, enrollment records, and compliance workflows are allocated immediately.
            </p>
          </div>
          <p>
            Failed payments may delay or suspend course access. Payment verification can take up to 24 hours in some cases. Chargebacks, disputed payments, or unauthorized payment reversals may result in immediate account suspension or termination.
          </p>
        </TermsBlock>

        <TermsBlock icon={BookOpenCheck} title="5. Course Completion and Certification">
          <TermsList items={completionRequirements} />
          <p>
            Digital certificates are issued after successful completion of all applicable program requirements. Certificates may include student details, program details, duration, issue date, certificate ID, and verification information.
          </p>
          <p>
            Certificates are intended to remain verifiable through Kampus Campanion. Replacement, correction, or reissued certificates may require verification and may incur additional charges where applicable.
          </p>
        </TermsBlock>

        <TermsBlock icon={ShieldAlert} title="6. User Conduct and Prohibited Activities">
          <TermsList items={prohibitedConduct} />
        </TermsBlock>

        <TermsBlock icon={FileText} title="7. Intellectual Property">
          <p>
            Course materials, platform design, software, content, documents, brand assets, certificates, and related resources are owned by Kampus Campanion, Dmaskut Private Limited, or their licensors and are protected by applicable intellectual property laws.
          </p>
          <p>
            You may not copy, distribute, resell, publicly share, modify, or create unauthorized derivative works from platform content. You retain rights to original assignment submissions, while granting Kampus Campanion permission to use submitted work for evaluation, academic records, quality review, platform improvement, and educational purposes.
          </p>
          <p>
            Kampus Campanion may use aggregated or anonymized data to improve programs, platform reliability, reporting, and learning outcomes.
          </p>
        </TermsBlock>

        <TermsBlock icon={Gavel} title="8. Suspension and Termination">
          <p>Kampus Campanion may suspend, restrict, or terminate your account and platform access for reasons including:</p>
          <TermsList items={terminationReasons} />
        </TermsBlock>

        <TermsBlock icon={Gavel} title="9. Limitation of Liability">
          <p>
            Kampus Campanion provides educational and internship management services. We do not guarantee employment, university admission, grades, scholarships, placements, salary outcomes, or specific career advancement.
          </p>
          <p>
            To the maximum extent permitted by law, Kampus Campanion's liability for claims related to paid services is limited to the program fee paid by the student for the relevant program.
          </p>
        </TermsBlock>

        <TermsBlock icon={Gavel} title="10. Governing Law and Disputes">
          <p>
            These terms are governed by the laws of India. Any disputes arising from or related to Kampus Campanion, these terms, payments, programs, certificates, or platform use shall be subject to the jurisdiction of courts in India.
          </p>
        </TermsBlock>

        <TermsBlock icon={FileText} title="11. Changes to Terms">
          <p>
            Kampus Campanion may update these terms from time to time. Continued use of the platform after changes are posted means you accept the updated terms.
          </p>
        </TermsBlock>

        <div className="bg-slate-900 text-white rounded-xl p-6 md:p-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-3">Contact Us</div>
          <h2 className="font-heading text-2xl font-bold">Terms, payments, and support contact</h2>
          <p className="text-slate-300 leading-relaxed mt-3 max-w-3xl">
            For questions about these terms, registration, payment issues, certificates, or support requests, contact Kampus Campanion.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-6 text-sm">
            <ContactItem icon={Mail} label="Email" value="support@kampuscompanion.com" />
            <ContactItem icon={Phone} label="Phone" value="+91 9217242305" />
            <ContactItem label="Address" value="Company Sarai, Rauza Road, Sasaram, Bihar - 821115" />
          </div>
          <Link to="/contact" className="inline-flex mt-6 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
            Go to Contact Page
          </Link>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

const TermsBlock = ({ icon: Icon, title, children }) => (
  <section className="bg-white border border-slate-200 rounded-xl p-6 md:p-8">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-blue-600" />
      </div>
      <h2 className="font-heading text-2xl font-bold text-slate-900">{title}</h2>
    </div>
    <div className="space-y-4 text-slate-600 leading-relaxed">{children}</div>
  </section>
);

const TermsList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item) => (
      <li key={item} className="flex gap-3">
        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-blue-600 flex-shrink-0"></span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const ContactItem = ({ icon: Icon, label, value }) => (
  <div className="bg-white/5 border border-white/10 rounded-lg p-4">
    <div className="flex items-center gap-2 text-blue-300 font-semibold">
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </div>
    <div className="text-slate-200 mt-2 leading-relaxed">{value}</div>
  </div>
);

export default TermsOfServicePage;

